export type AttributionTouch = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  landing_page?: string;
  referrer?: string;
};

export type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
};

const FIRST_KEY = "bs_attr_first";
const LAST_KEY = "bs_attr_last";
const CONSENT_KEY = "cookiePreferences";
const CONSENT_DECIDED_KEY = "cookiePreferencesDecided";

export const AD_LANDING_PATHS = ["/lessons", "/academy", "/dla-nauczycieli"] as const;
export const META_PIXEL_ID = "1636973557961695";
export const GA_MEASUREMENT_ID = "G-868BE0PCPJ";

const ATTR_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "ttclid",
] as const;

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  loaded?: boolean;
  version?: string;
  push: Fbq;
};

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
    fbq?: Fbq;
    _fbq?: Fbq;
    __bsGaInitialized?: boolean;
  }
}

export function getCookiePreferences(): CookiePreferences {
  if (typeof window === "undefined") {
    return { necessary: true, analytics: false, marketing: false, preferences: false };
  }
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (raw) {
      return {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
        ...JSON.parse(raw),
      };
    }
  } catch {
    // ignore
  }
  return { necessary: true, analytics: false, marketing: false, preferences: false };
}

export function hasDecidedCookiePreferences(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(CONSENT_DECIDED_KEY) === "1" || !!localStorage.getItem(CONSENT_KEY);
}

export function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

export function isAdLandingPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  if ((AD_LANDING_PATHS as readonly string[]).includes(path)) return true;
  return path === "/warszawa/sale-prob" || path.startsWith("/warszawa/sale-prob/");
}

export function shouldLoadMetaPixel(
  pathname = typeof window !== "undefined" ? window.location.pathname : "/",
): boolean {
  if (typeof window === "undefined") return isAdLandingPath(pathname);
  const decided = hasDecidedCookiePreferences();
  const prefs = getCookiePreferences();
  if (decided) return !!prefs.marketing;
  return isAdLandingPath(pathname);
}

/** Official Meta Pixel loader. PageView is sent once on first init. */
export function initMetaPixel(): void {
  if (typeof window === "undefined" || !shouldLoadMetaPixel()) return;
  if (window.fbq) return;

  const n = function (...args: unknown[]) {
    if (n.callMethod) n.callMethod(...args);
    else n.queue.push(args);
  } as Fbq;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  window.fbq = n;
  window._fbq = n;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(script, first);

  n("init", META_PIXEL_ID);
  n("track", "PageView");
}

export function trackMetaPageView(): void {
  if (typeof window === "undefined" || !shouldLoadMetaPixel()) return;
  if (!window.fbq) {
    initMetaPixel();
    return;
  }
  window.fbq("track", "PageView");
}

function createEventId(prefix: string): string {
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}.${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${id}`;
}

function trackMetaEvent(
  event: string,
  params: Record<string, unknown> = {},
  eventId?: string,
): string | undefined {
  if (typeof window === "undefined" || !shouldLoadMetaPixel()) return undefined;
  initMetaPixel();
  if (!window.fbq) return undefined;
  const id = eventId || createEventId(event.toLowerCase());
  window.fbq("track", event, params, { eventID: id });
  return id;
}

export function shouldLoadGoogleAnalytics(
  pathname = typeof window !== "undefined" ? window.location.pathname : "/",
): boolean {
  if (typeof window === "undefined") return isAdLandingPath(pathname);
  const decided = hasDecidedCookiePreferences();
  const prefs = getCookiePreferences();
  if (decided) return !!prefs.analytics;
  return isAdLandingPath(pathname);
}

function ensureGtag(): void {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    ((...args: unknown[]) => {
      window.dataLayer!.push(args as unknown as Record<string, unknown>);
    });
}

/**
 * Ad landings hide the cookie banner, same as the Meta Pixel.
 * Grant storage so those hits are stored instead of dropped by Consent Mode.
 */
function grantGoogleConsentForAdLanding(): void {
  if (hasDecidedCookiePreferences()) return;
  if (!isAdLandingPath(window.location.pathname)) return;
  ensureGtag();
  window.gtag!("consent", "update", {
    analytics_storage: "granted",
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
}

function gaScriptPresent(): boolean {
  return !!document.querySelector(`script[src*="gtag/js?id=${GA_MEASUREMENT_ID}"]`);
}

/**
 * Direct GA4 loader. GTM already installs this measurement ID, so this only
 * configs the tag when that script never arrives.
 */
export function initGoogleAnalytics(): void {
  if (typeof window === "undefined" || !shouldLoadGoogleAnalytics()) return;
  ensureGtag();
  grantGoogleConsentForAdLanding();
  if (window.__bsGaInitialized) return;
  window.__bsGaInitialized = true;
  if (gaScriptPresent()) return;

  window.setTimeout(() => {
    if (gaScriptPresent()) return;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    const first = document.getElementsByTagName("script")[0];
    first?.parentNode?.insertBefore(script, first);
    window.gtag!("js", new Date());
    window.gtag!("config", GA_MEASUREMENT_ID);
  }, 2000);
}

export function trackGooglePageView(): void {
  if (typeof window === "undefined" || !shouldLoadGoogleAnalytics()) return;
  if (!window.__bsGaInitialized) {
    initGoogleAnalytics();
    return;
  }
  window.gtag?.("event", "page_view", {
    page_path: window.location.pathname + window.location.search + window.location.hash,
    page_location: window.location.href,
    page_title: document.title,
  });
}

function trackGoogleEvent(event: string, params: Record<string, unknown> = {}): void {
  if (typeof window === "undefined" || !shouldLoadGoogleAnalytics()) return;
  initGoogleAnalytics();
  window.gtag?.("event", event, params);
}

export function saveCookiePreferences(prefs: CookiePreferences): void {
  const next = { ...prefs, necessary: true };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(next));
    localStorage.setItem(CONSENT_DECIDED_KEY, "1");
  } catch (error) {
    console.error("Failed to persist cookie preferences", error);
    throw error;
  }
  try {
    applyGoogleConsent(next);
    if (next.marketing) {
      initMetaPixel();
    }
    if (next.analytics) {
      initGoogleAnalytics();
    }
    pushDataLayerEvent("cookie_consent_update", {
      analytics: next.analytics,
      marketing: next.marketing,
      preferences: next.preferences,
    });
  } catch (error) {
    console.error("Failed to apply Google consent update", error);
  }
}

export function applyGoogleConsent(prefs: CookiePreferences = getCookiePreferences()): void {
  if (typeof window === "undefined") return;
  const analytics = prefs.analytics ? "granted" : "denied";
  const marketing = prefs.marketing ? "granted" : "denied";
  window.dataLayer = window.dataLayer || [];
  const gtag =
    window.gtag ||
    ((...args: unknown[]) => {
      window.dataLayer!.push(args as unknown as Record<string, unknown>);
    });
  window.gtag = gtag;
  gtag("consent", "update", {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  });
}

export function initGoogleConsentDefaults(): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  const gtag = (...args: unknown[]) => {
    window.dataLayer!.push(args as unknown as Record<string, unknown>);
  };
  window.gtag = gtag;
  const decided = hasDecidedCookiePreferences();
  const prefs = getCookiePreferences();
  gtag("consent", "default", {
    analytics_storage: decided && prefs.analytics ? "granted" : "denied",
    ad_storage: decided && prefs.marketing ? "granted" : "denied",
    ad_user_data: decided && prefs.marketing ? "granted" : "denied",
    ad_personalization: decided && prefs.marketing ? "granted" : "denied",
    wait_for_update: 500,
  });
}

function readTouch(key: string): AttributionTouch | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    return JSON.parse(raw) as AttributionTouch;
  } catch {
    return undefined;
  }
}

function writeTouch(key: string, touch: AttributionTouch): void {
  localStorage.setItem(key, JSON.stringify(touch));
}

function referrerHostname(referrer?: string): string | undefined {
  if (!referrer) return undefined;
  try {
    return new URL(referrer).hostname.toLowerCase().replace(/^www\./, "") || undefined;
  } catch {
    return undefined;
  }
}

function isBandSpaceHostname(hostname?: string): boolean {
  return !!hostname && (hostname === "band-space.com" || hostname.endsWith(".band-space.com"));
}

function canonicalSocialSource(source: string): string {
  const normalized = source.toLowerCase();
  if (normalized === "ig" || normalized === "instagram" || normalized.endsWith(".instagram.com")) return "instagram";
  if (["fb", "meta", "facebook"].includes(normalized) || normalized.endsWith(".facebook.com")) return "facebook";
  if (normalized === "threads" || normalized === "threads.net" || normalized.endsWith(".threads.net")) return "threads";
  if (normalized === "tiktok" || normalized === "tiktok.com" || normalized.endsWith(".tiktok.com")) return "tiktok";
  return normalized;
}

/** Fill a missing/synthetic source without overwriting explicit campaign UTMs. */
function normalizeTouch(touch: AttributionTouch): AttributionTouch {
  const normalized = { ...touch };
  const source = normalized.utm_source?.trim().toLowerCase();
  if (source) normalized.utm_source = canonicalSocialSource(source);
  const syntheticSource = !source || source === "direct" || source === "referral";
  const hostname = referrerHostname(normalized.referrer);

  if (syntheticSource && normalized.gclid) {
    normalized.utm_source = "google";
    normalized.utm_medium = "cpc";
  } else if (syntheticSource && normalized.fbclid) {
    normalized.utm_source = "facebook";
    normalized.utm_medium = "social";
  } else if (syntheticSource && normalized.ttclid) {
    normalized.utm_source = "tiktok";
    normalized.utm_medium = "social";
  } else if (syntheticSource && hostname && !isBandSpaceHostname(hostname)) {
    normalized.utm_source = canonicalSocialSource(hostname);
    normalized.utm_medium = "referral";
  } else if (syntheticSource) {
    normalized.utm_source = "direct";
    normalized.utm_medium = "none";
  }

  return normalized;
}

function touchHasSignal(touch: AttributionTouch): boolean {
  return ATTR_PARAMS.some((k) => !!touch[k]);
}

export function captureAttributionFromLocation(
  search = typeof window !== "undefined" ? window.location.search : "",
  pathname = typeof window !== "undefined" ? window.location.pathname : "/",
): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(search);
  const touch: AttributionTouch = {};
  for (const key of ATTR_PARAMS) {
    const value = params.get(key);
    if (value) touch[key] = value;
  }
  touch.landing_page = `${pathname}${search}` || "/";
  if (document.referrer) touch.referrer = document.referrer;

  if (!touchHasSignal(touch) && !readTouch(FIRST_KEY)) {
    const organic = normalizeTouch({
      landing_page: touch.landing_page,
      referrer: touch.referrer,
    });
    writeTouch(FIRST_KEY, organic);
    writeTouch(LAST_KEY, organic);
    return;
  }
  if (!touchHasSignal(touch)) return;

  const prefs = getCookiePreferences();
  if (!prefs.marketing) {
    delete touch.gclid;
    delete touch.fbclid;
    delete touch.ttclid;
  }

  const normalizedTouch = normalizeTouch(touch);

  if (!readTouch(FIRST_KEY)) writeTouch(FIRST_KEY, normalizedTouch);
  writeTouch(LAST_KEY, normalizedTouch);
}

export function appendAttributionToUrl(url: string): string {
  if (typeof window === "undefined") return url;
  try {
    const u = new URL(url, window.location.origin);
    const stored = readTouch(LAST_KEY) || readTouch(FIRST_KEY);
    const last = stored ? normalizeTouch(stored) : undefined;
    if (!last) return url;
    for (const key of ATTR_PARAMS) {
      const value = last[key];
      if (value && !u.searchParams.has(key)) {
        u.searchParams.set(key, value);
      }
    }
    return u.toString();
  } catch {
    return url;
  }
}

export function pushDataLayerEvent(
  event: string,
  params: Record<string, unknown> = {},
): void {
  if (typeof window === "undefined") return;
  const prefs = getCookiePreferences();
  if (!prefs.analytics && event !== "cookie_consent_update") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
}

export type StartBookingSourcePage = "warsaw_listing" | "studio_profile";

export function trackStartBooking(params: {
  provider_id: string;
  source_page: StartBookingSourcePage;
  provider_type?: "studio";
  city?: string;
  provider_name?: string;
}): void {
  const eventId = createEventId("initiatecheckout");
  const bookingParams = {
    provider_type: params.provider_type ?? "studio",
    provider_id: params.provider_id,
    source_page: params.source_page,
    city: params.city ?? "warsaw",
    event_id: eventId,
    ...(params.provider_name ? { provider_name: params.provider_name } : {}),
  };
  pushDataLayerEvent("start_booking", bookingParams);
  trackGoogleEvent("start_booking", bookingParams);
  trackMetaEvent(
    "InitiateCheckout",
    {
      content_category: "rehearsal_room",
      content_ids: [params.provider_id],
      content_name: params.provider_name,
      city: params.city ?? "warsaw",
      source_page: params.source_page,
    },
    eventId,
  );
}
